import { useCallback, useEffect, useMemo, useState } from "react";

import { getModelDescriptorFromObject } from "@doitintl/cloudflow-commons";
import { type CloudFlowNodeType, NODE_STATUS, type UnwrappedApiServiceModelDescriptor } from "@doitintl/cmp-models";
import OpenNewIcon from "@mui/icons-material/OpenInNewRounded";
import WebhookIcon from "@mui/icons-material/Webhook";
import { Box, Button, Card, Link, Stack, TextField, Typography } from "@mui/material";
import debounce from "lodash/debounce";

import { cloudflowTexts } from "../../../../../assets/texts";
import { CopyCodeBlock } from "../../../../../Components/CopyCodeBlock/CopyCodeBlock";
import Tabs, { type TabConfig } from "../../../../../Components/Tabs";
import { useModalManager } from "../../Common/CloudflowModalsProvider";
import { useCloudflowOperations } from "../../Common/CloudflowOperationsProvider";
import { useNodeConfigurationContext } from "../NodeConfigurationContext";
import ModelDescriptorTableStructure from "./ModelDescriptorTableStructure";
import { NodeCardHeader } from "./NodeCardHeader";

type SampleJsonTabProps = {
  jsonInput: string;
  error: string;
  onJsonChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
};

const SampleJsonTab = ({ jsonInput, error, onJsonChange }: SampleJsonTabProps) => (
  <Stack gap={2} sx={{ mt: 2 }}>
    <Typography variant="body2" color="text.secondary">
      {cloudflowTexts.WEBHOOK_TRIGGER.SAMPLE_JSON_TAB_TEXT}
    </Typography>
    <TextField
      multiline
      minRows={8}
      value={jsonInput}
      onChange={onJsonChange}
      error={!!error}
      helperText={error}
      sx={{
        "& .MuiInputBase-root": {
          fontFamily: "monospace",
          bgcolor: "general.backgroundDark",
        },
      }}
    />
  </Stack>
);

type DetectedStructureTabProps = {
  modelDescriptor: UnwrappedApiServiceModelDescriptor | null;
};

const DetectedStructureTab = ({ modelDescriptor }: DetectedStructureTabProps) => (
  <Stack gap={2} sx={{ mt: 2 }}>
    <Typography variant="body2" color="text.secondary">
      {cloudflowTexts.WEBHOOK_TRIGGER.DETECTED_STRUCTURE_TAB_TEXT}
    </Typography>
    <ModelDescriptorTableStructure model={modelDescriptor} />
  </Stack>
);

const parseJSONInput = (jsonStr: string) => {
  if (!jsonStr || jsonStr?.trim() === "") {
    return { data: "", error: "Sample JSON payload is required" };
  }
  try {
    JSON.parse(jsonStr);
    return { data: jsonStr, error: "" };
  } catch (err) {
    const errorMsg = (err as Error).message;
    return { data: jsonStr, error: errorMsg };
  }
};

export const WebhookTriggerTab = () => {
  const { httpOperationLoading } = useCloudflowOperations();
  const { openModal } = useModalManager();
  const { updateNode, nodeConfig } = useNodeConfigurationContext<CloudFlowNodeType.WEBHOOK_TRIGGER>();
  const [jsonData, setJsonData] = useState<{ data: string; error: string }>(() => ({
    ...parseJSONInput(nodeConfig.parameters.payloadModel),
    error: "", // On initial render dont show an error
  }));

  const modelDescriptor = useMemo(() => {
    try {
      const data = JSON.parse(nodeConfig.parameters.payloadModel);
      return getModelDescriptorFromObject(data);
    } catch {
      return null;
    }
  }, [nodeConfig.parameters.payloadModel]);

  const debouncedUpdateNode = useMemo(
    () =>
      debounce((jsonStr: string, error: string) => {
        updateNode((prev) => ({
          ...prev,
          parameters: { payloadModel: jsonStr },
          status: error ? NODE_STATUS.ERROR : NODE_STATUS.VALIDATED,
          errors: error ? { ...prev.errors, param_error: error } : { ...prev.errors, param_error: undefined },
        }));
      }, 500),
    [updateNode]
  );

  useEffect(
    () => () => {
      debouncedUpdateNode.cancel();
    },
    [debouncedUpdateNode]
  );

  const handleJsonChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const inputValue = event.target.value;
      const res = parseJSONInput(inputValue);
      setJsonData(res);
      debouncedUpdateNode(inputValue, res.error);
    },
    [debouncedUpdateNode]
  );

  const tabs: TabConfig[] = [
    {
      label: "Sample JSON",
      content: <SampleJsonTab jsonInput={jsonData.data} error={jsonData.error} onJsonChange={handleJsonChange} />,
      key: "sample-json",
    },
    {
      label: "Detected structure",
      content: <DetectedStructureTab modelDescriptor={modelDescriptor} />,
      key: "detected-structure",
    },
  ];

  return (
    <Stack
      sx={{
        p: 2,
        justifyContent: "center",
        gap: 2,
      }}
    >
      <Card sx={{ "&:hover": { borderColor: "primary.main" } }}>
        <NodeCardHeader
          avatar={<WebhookIcon color="primary" />}
          subheader="Start a flow when an external event happens"
          title="Webhook"
          action={
            <Button
              disabled={httpOperationLoading}
              onClick={() => {
                openModal("trigger");
              }}
            >
              Change
            </Button>
          }
        />
      </Card>
      <Typography variant="subtitle2">Webhook URL</Typography>
      <Typography variant="body2" color="text.secondary">
        Provide this URL to the service that will send data to trigger this CloudFlow. The webhook should be invoked
        using the POST method.
        <Link
          href="https://developer.doit.com/docs/start#api-keys"
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          <Box sx={{ display: "inline", marginLeft: 0.5 }}>
            Learn more
            <OpenNewIcon color="primary" sx={{ fontSize: 16, marginLeft: 0.25, verticalAlign: "middle" }} />
          </Box>
        </Link>
      </Typography>

      <CopyCodeBlock base={`https://api.doit.com/cloudflow/v1/trigger/${nodeConfig.flowId}`} maxHeight={200} />

      <Typography variant="subtitle2">Allow referencing of webhook values</Typography>
      <Typography variant="body2" color="text.secondary">
        In order to select fields from the webhook in this CloudFlow we need to understand the data structure.
      </Typography>

      <Tabs tabs={tabs} variant="fullWidth" />
    </Stack>
  );
};
